import { useEffect, useRef } from "react";

import { DrawerContainer, SidePanelContainer } from "components/window";
import { drawersActions, useDispatchDrawers, useOpenDrawer } from "contexts/drawers";
import { usePanelMode } from "contexts/panelMode";
import { sidePanelsActions, useDispatchSidePanels, useSidePanels } from "contexts/sidePanels";

/**
 * Panel container that renders SidePanel or Drawer based on screen size.
 * Phones (short dimension ≤ 448px): Drawers only.
 * Tablets/Desktop: SidePanels only.
 *
 * When the mode changes, the currently open panel ID is carried over to the
 * incoming system so the user doesn't lose their context on resize.
 */
export function AdaptivePanelContainer() {
    const panelMode = usePanelMode();
    const { openPanels } = useSidePanels();
    const openDrawer = useOpenDrawer();
    const dispatchSidePanels = useDispatchSidePanels();
    const dispatchDrawers = useDispatchDrawers();

    const openPanelsRef = useRef(openPanels);
    openPanelsRef.current = openPanels;
    const openDrawerRef = useRef(openDrawer);
    openDrawerRef.current = openDrawer;
    const prevModeRef = useRef<typeof panelMode | null>(null);

    useEffect(() => {
        const prevMode = prevModeRef.current;
        prevModeRef.current = panelMode;

        if (prevMode === null || prevMode === panelMode) {
            return;
        }

        if (panelMode === "drawer") {
            // sidePanel → drawer: open the most recently open side panel as a drawer
            const lastOpen = openPanelsRef.current[openPanelsRef.current.length - 1];
            if (lastOpen) {
                dispatchDrawers(drawersActions.openDrawer(lastOpen));
            }
            dispatchSidePanels(sidePanelsActions.closeAllPanels());
        } else {
            // drawer → sidePanel: open the drawer's panel as a side panel
            const currentDrawer = openDrawerRef.current;
            if (currentDrawer) {
                dispatchSidePanels(sidePanelsActions.openPanel(currentDrawer.id));
            }
            dispatchDrawers(drawersActions.closeDrawer());
        }
    }, [panelMode, dispatchDrawers, dispatchSidePanels]);

    if (panelMode === "sidePanel") {
        return <SidePanelContainer />;
    } else {
        return <DrawerContainer />;
    }
}
